import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Cable, Wifi, WifiOff, Volume2 } from 'lucide-react';
import { IntercomDeviceEngine, TEST_TONE_DEVICE_ID } from '../lib/intercom-device-engine.js';
import { api } from '../lib/api.js';

/**
 * „Web-Intercom"-Gerät im Browser: sendet Mikrofon in die Intercom (ext-mic)
 * und spielt ab, was Panels an dieses Gerät schicken (ext-spk) – ohne Desktop.
 */
export function IntercomDeviceView(): JSX.Element {
  const { deviceId } = useParams<{ deviceId: string }>();
  const engineRef = useRef<IntercomDeviceEngine | null>(null);
  if (!engineRef.current) engineRef.current = new IntercomDeviceEngine();
  const engine = engineRef.current;

  const [name, setName] = useState<string>('Web-Intercom');
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [micId, setMicId] = useState<string>('');
  const [gain, setGain] = useState(1);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [level, setLevel] = useState(0);
  const [incoming, setIncoming] = useState(0);

  useEffect(() => {
    if (!deviceId) return;
    api
      .get<{ name: string }>(`/api/v1/external-devices/${deviceId}/public`)
      .then((d) => setName(d.name))
      .catch(() => {});
  }, [deviceId]);

  useEffect(() => {
    engine.onChange = () => {
      setConnected(engine.connected);
      setIncoming(engine.incomingCount);
    };
    const t = window.setInterval(() => {
      setLevel(engine.level);
      setIncoming(engine.incomingCount);
    }, 120);
    return () => {
      engine.onChange = () => {};
      window.clearInterval(t);
      void engine.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshDevices(): Promise<void> {
    try {
      const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
      probe.getTracks().forEach((t) => t.stop());
    } catch {
      /* */
    }
    const all = await navigator.mediaDevices.enumerateDevices();
    setDevices(all.filter((d) => d.kind === 'audioinput'));
  }
  useEffect(() => {
    void refreshDevices();
  }, []);

  async function onConnect(): Promise<void> {
    if (!deviceId) return;
    setBusy(true);
    setError(null);
    try {
      await engine.connect(deviceId, micId, gain);
      setConnected(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function onDisconnect(): Promise<void> {
    setBusy(true);
    try {
      await engine.disconnect();
    } finally {
      setConnected(false);
      setBusy(false);
    }
  }
  async function onMic(id: string): Promise<void> {
    setMicId(id);
    setError(null);
    try {
      await engine.setDevice(id);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (!deviceId) return <Fallback message="Keine Geräte-ID in der URL." />;

  return (
    <div className="min-h-full flex flex-col" onPointerDown={() => engine.unlock()}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface">
        <div className="flex items-center gap-2">
          <Cable size={18} className="text-accent" />
          <h1 className="text-sm font-semibold text-text">{name}</h1>
          <span className="text-xxs uppercase tracking-wider text-text-mute">Web-Intercom</span>
        </div>
        <div className="flex items-center gap-2 text-xxs uppercase tracking-wider">
          {connected ? (
            <>
              <Wifi size={14} className="text-ok" />
              <span className="text-text-mute">aktiv · {incoming} rein</span>
            </>
          ) : (
            <>
              <WifiOff size={14} className="text-err" />
              <span className="text-text-mute">aus</span>
            </>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 max-w-md w-full mx-auto space-y-5">
        <p className="text-sm text-text-dim">
          Bidirektionale externe Intercom im Browser: dein Mikrofon geht in die Intercom, und was
          Panels an dieses Gerät senden, hörst du hier. Routing wie ein internes Gerät (im Admin
          unter „Externe Quellen" gepaart).
        </p>

        <div className="card p-4 space-y-3">
          <div className="text-xxs uppercase tracking-wider text-text-mute">Mikrofon</div>
          <select
            className="w-full px-2 py-2 rounded bg-surface text-text text-sm border border-border"
            value={micId}
            onChange={(e) => void onMic(e.target.value)}
          >
            <option value="">— System-Standard —</option>
            <option value={TEST_TONE_DEVICE_ID}>🔊 Test-Ton (1 kHz)</option>
            {devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || d.deviceId.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>

        <div className="card p-4 space-y-3">
          <div className="text-xxs uppercase tracking-wider text-text-mute">
            Mikrofon-Pegel (Gain {(gain * 100).toFixed(0)} %)
          </div>
          <input
            type="range"
            min={0}
            max={2}
            step={0.01}
            value={gain}
            onChange={(e) => {
              const g = parseFloat(e.target.value);
              setGain(g);
              engine.setGain(g);
            }}
            className="w-full"
          />
          <div className="h-2 w-full rounded-full bg-black/40 overflow-hidden">
            <div
              className="h-full bg-accent transition-[width] duration-75"
              style={{ width: `${Math.min(100, level * 140)}%` }}
            />
          </div>
        </div>

        {connected ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void onDisconnect()}
            className="w-full py-3 rounded-btn bg-err text-white font-semibold text-sm disabled:opacity-50"
          >
            Verbindung trennen
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => void onConnect()}
            className="w-full py-3 rounded-btn bg-accent text-black font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Volume2 size={18} />
            {busy ? 'Verbinde…' : 'Verbinden (senden + empfangen)'}
          </button>
        )}

        {error ? <div className="text-xs text-err break-words">Fehler: {error}</div> : null}
      </div>
    </div>
  );
}

function Fallback({ message }: { message: string }): JSX.Element {
  return (
    <div className="min-h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-text-dim text-sm">{message}</p>
    </div>
  );
}
